import React, { useState, useEffect } from 'react';
import { useLang } from '../../contexts/LangContext';
import { ApartmentFeeCategory } from '../../api/apartmentsApi';

interface EditFeeCategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  feeCategory: ApartmentFeeCategory | null;
  onSave: (feeCategoryId: string, quantity: number, notes: string) => Promise<void>;
}

const EditFeeCategoryModal: React.FC<EditFeeCategoryModalProps> = ({
  isOpen,
  onClose,
  feeCategory,
  onSave,
}) => {
  // const { t } = useLang(); // Removed unused variable
  const [quantity, setQuantity] = useState<number>(0);
  const [notes, setNotes] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (feeCategory) {
      setQuantity(feeCategory.quantity);
      setNotes(feeCategory.notes || '');
    }
  }, [feeCategory]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!feeCategory) return;

    setLoading(true);
    setError(null);

    try {
      await onSave(feeCategory.feeCategoryId, quantity, notes);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Có lỗi xảy ra khi cập nhật');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setQuantity(0);
    setNotes('');
    setError(null);
    onClose();
  };

  if (!isOpen || !feeCategory) return null;

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('vi-VN').format(price);
  };

  // Usage-based is determined by isMonthlyFixed = false
  const isUsageBased = !feeCategory.feeCategory.isMonthlyFixed;
  // Tiered pricing (Bật thang) determines if there are multiple price levels
  const isTieredPricing = feeCategory.feeCategory.pricingType === 'TIERED';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-screen overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            Cập nhật thông tin phí dịch vụ
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Fee Category Info (Read-only) */}
          <div className="space-y-4">
            {/* Basic Info */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tên phí dịch vụ</label>
              <input
                type="text"
                value={feeCategory.feeCategory.name}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700"
              />
            </div>

            {/* Tiered Pricing Display - Bật thang */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Đơn giá bậc thang</label>
              <div className="flex items-center space-x-2">
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                  isTieredPricing 
                    ? 'border-orange-400 bg-orange-100' 
                    : 'border-gray-300 bg-gray-50'
                }`}>
                  {isTieredPricing && (
                    <div className="w-2 h-2 rounded-full bg-orange-400"></div>
                  )}
                </div>
                <span className="text-sm text-gray-700">
                  {isTieredPricing ? 'Đã bật' : 'Chưa bật'}
                </span>
              </div>
            </div>

            {/* Show pricing details based on tiered pricing */}
            {isTieredPricing ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mức sử dụng</label>
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-4 text-sm font-medium text-gray-600">
                    <div>Từ - Đến</div>
                    <div>Giá tiền</div>
                  </div>
                  {feeCategory.feeCategory.feeTiers && feeCategory.feeCategory.feeTiers.map((tier, index) => (
                    <div key={tier.id} className="grid grid-cols-2 gap-4">
                      <input
                        type="text"
                        value={`${tier.usageFrom || 0} - ${tier.usageTo || '∞'}`}
                        disabled
                        className="px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700 text-sm"
                      />
                      <input
                        type="text"
                        value={formatPrice(tier.price)}
                        disabled
                        className="px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700 text-sm"
                      />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Đơn giá (chưa gồm VAT - nếu có)</label>
                <input
                  type="text"
                  value={feeCategory.feeCategory.basePrice ? formatPrice(feeCategory.feeCategory.basePrice) : '0'}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phí VAT (%)</label>
              <input
                type="text"
                value={feeCategory.feeCategory.vatPercentage || 0}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700"
              />
            </div>

            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                  feeCategory.feeCategory.isMonthlyFixed 
                    ? 'border-green-400 bg-green-100' 
                    : 'border-gray-300 bg-gray-50'
                }`}>
                  {feeCategory.feeCategory.isMonthlyFixed && (
                    <div className="w-2 h-2 rounded-full bg-green-400"></div>
                  )}
                </div>
                <span className="text-sm text-gray-700">Phí cố định hàng tháng</span>
              </div>

              <div className="flex items-center space-x-2">
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                  feeCategory.feeCategory.autoCloseMonthly 
                    ? 'border-green-400 bg-green-100' 
                    : 'border-gray-300 bg-gray-50'
                }`}>
                  {feeCategory.feeCategory.autoCloseMonthly && (
                    <div className="w-2 h-2 rounded-full bg-green-400"></div>
                  )}
                </div>
                <span className="text-sm text-gray-700">Tự động tính phí hàng tháng</span>
              </div>
            </div>
          </div>

          {/* Editable Fields */}
          <div>
            <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-1">
              Số lượng {!isUsageBased && <span className="text-red-500">*</span>}
            </label>
            <input
              type="number"
              id="quantity"
              value={quantity}
              onChange={(e) => setQuantity(parseFloat(e.target.value) || 0)}
              min="0"
              step="0.01"
              disabled={isUsageBased}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isUsageBased ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
              required={!isUsageBased}
            />
            {isUsageBased && (
              <p className="text-xs text-gray-500 mt-1">
                Không thể chỉnh sửa số lượng cho phí theo mức sử dụng
              </p>
            )}
          </div>

          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
              Ghi chú
            </label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Thông tin ghi chú"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Hủy
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? 'Đang lưu...' : 'Lưu'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditFeeCategoryModal;